/**
 * Customer Warehouse Summary API
 * Client-side functions to call the new optimized warehouse summary
 */

import { getSessionUUID } from '../components/lib/AuthToken';

/**
 * Get complete warehouse summary for a customer using the optimized RPC function
 * @param {number} customerId - The customer ID
 * @param {AbortSignal} [abortSignal] - Optional abort signal to cancel the request
 * @returns {Promise<Object>} Warehouse summary with products and pallet codes
 */
export const getCustomerWarehouseSummary = async (customerId, abortSignal = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                customer_id: customerId
            })
        };

        // Add abort signal if provided
        if (abortSignal) {
            fetchOptions.signal = abortSignal;
        }

        const response = await fetch('/enhanced_warehouse/warehouse/summary', fetchOptions);

        const data = await response.json();
        
        if (response.ok) {
            return {
                status: response.status,
                success: true,
                data: data
            };
        } else {
            return {
                status: response.status,
                success: false,
                error: data.error || 'Failed to fetch warehouse summary'
            };
        }
    } catch (error) {
        console.error('Error fetching customer warehouse summary:', error);
        return {
            status: 500,
            success: false,
            error: 'Network error while fetching warehouse summary'
        };
    }
};

/**
 * Get pallet details for a specific product with pagination
 * @param {string} productId - The product ID
 * @param {number} customerId - The customer ID  
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 100)
 * @returns {Promise<Object>} Pallet details for the product
 */
export const getProductPalletDetails = async (productId, customerId, page = 1, pageSize = 100) => {
    try {
        const sessionToken = await getSessionUUID();

        const response = await fetch('/enhanced_warehouse/warehouse/product-pallets', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                product_id: productId,
                customer_id: customerId,
                page: page,
                page_size: pageSize
            })
        });

        const data = await response.json();
        
        if (response.ok) {
            return {
                status: response.status,
                success: true,
                data: data
            };
        } else {
            return {
                status: response.status,
                success: false,
                error: data.error || 'Failed to fetch product pallet details'
            };
        }
    } catch (error) {
        console.error('Error fetching product pallet details:', error);
        return {
            status: 500,
            success: false,
            error: 'Network error while fetching product pallet details'
        };
    }
};